import { useMemo } from 'react';

import { SuggestionAcknowledgementProvider } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.provider';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useWaterfallRouteContext } from '@/modules/entities/contexts/waterfallRoute/waterfallRoute.context';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenBodySkeleton } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenBodySkeleton';
import { EditEntitySplitScreenFooterActionsPlaceholder } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFooter';
import { EntityType } from '@/modules/entities/types/EntityType';
import { isHypotheticalWaterfall } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import { getNodes } from '@/utils/graphqlUtils';

import { BeneficiaryDesignationsForm } from './BeneficiaryDesignationsForm';
import { getInitialFormValues } from './BeneficiaryDesignationsForm.utils';
import { extractBdgData } from './BeneficiaryDesignationsModalBody.utils';
import { useGetBeneficiaryDesignationsQuery } from './graphql/BeneficiaryDesignations.generated';

interface BeneficiaryDesignationsModalBodyProps {
  onClose: () => void;
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  /** Called after a successful persist; see {@link useSubmitBeneficiaryDesignations}. */
  onDidSave?: () => void;
  entityType: EntityType;
  /**
   * When the BDG body is opened for an entity other than the modal's root
   * (e.g. a non-root BDG-backed tile clicked from a DP form's diagram),
   * pass the target's entityId and displayName so the body operates on
   * that entity instead of the URL/context root.
   */
  entityIdOverride?: string;
  displayNameOverride?: string;
  /**
   * Non-root only: the parent DP diagram's active grantor death id, so
   * the BDG form's prefetch / scenario picks the same death-order
   * scenario the parent diagram is rendering.
   */
  firstDeathClientIdOverride?: string;
}

export function BeneficiaryDesignationsModalBody({
  onClose,
  onAfterSubmit,
  onDidSave,
  entityType,
  entityIdOverride,
  displayNameOverride,
  firstDeathClientIdOverride,
}: BeneficiaryDesignationsModalBodyProps) {
  const { entityId: contextEntityId } = useEntityDetailsContext();
  const { footerRef } = useEditEntitySplitScreenModalContext();
  const entityId = entityIdOverride ?? contextEntityId;
  // The BDG editor is opened from both the live diagram and from any
  // hypothetical estate waterfall page. WaterfallRouteProvider is mounted on
  // every waterfall route (diagram, summary list, summary, tax overview,
  // beneficiary report), so reading the id from there works everywhere a
  // hypothetical scope applies. Outside a waterfall route the context default
  // returns `waterfallId: null`, so we operate on live BDGs.
  const { waterfallId: routeWaterfallId } = useWaterfallRouteContext();
  const waterfallId = routeWaterfallId ?? undefined;
  const isOnWaterfall = !!waterfallId;

  const { data, loading } = useGetBeneficiaryDesignationsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      entityId: entityId ?? '',
      // When not on a waterfall the hypothetical selection is skipped via
      // @include(if: $isOnWaterfall), so the where-clause value is unused.
      hypotheticalWaterfallWhere: { id: waterfallId },
      isOnWaterfall,
    },
    skip: !entityId,
  });

  const waterfall = useMemo(() => {
    return getNodes(data?.hypotheticalWaterfall)[0] ?? null;
  }, [data?.hypotheticalWaterfall]);

  // The route gives us a waterfallId for ANY waterfall (base or hypothetical),
  // but BDG hypothetical persistence and the callout only apply to hypothetical
  // waterfalls. Check the kind to avoid treating Base-waterfall routes as
  // hypothetical scopes.
  const isOnHypotheticalWaterfall = isHypotheticalWaterfall(waterfall);

  const { bdgs, entitySubtypeId, isInEstate, accountDecedentClientId } =
    useMemo(() => {
      return extractBdgData(data, isOnHypotheticalWaterfall);
    }, [data, isOnHypotheticalWaterfall]);

  const initialFormValues = useMemo(() => {
    return getInitialFormValues(bdgs, isInEstate);
  }, [bdgs, isInEstate]);

  // On a waterfall route the form's scope (live vs hypothetical) is derived
  // from the resolved waterfall kind. With cache-and-network the entity `node`
  // can arrive from cache before `hypotheticalWaterfall` does, which would
  // briefly resolve the scope as live and could route a save to the live BDGs.
  // Wait until the waterfall lookup has resolved before rendering the form.
  const isWaterfallScopeResolved =
    !isOnWaterfall || !!data?.hypotheticalWaterfall;

  // The footer's primary actions are portaled from the form, so they're absent
  // while it loads; keep disabled stand-ins present so they don't blink out.
  const loadingState = (
    <>
      <EditEntitySplitScreenBodySkeleton />
      <EditEntitySplitScreenFooterActionsPlaceholder footerRef={footerRef} />
    </>
  );

  if ((loading && !data) || !isWaterfallScopeResolved) {
    return loadingState;
  }

  if (!entitySubtypeId) {
    return loadingState;
  }

  return (
    // Holds the AI beneficiary-designation suggestions the user accepts/ignores
    // during this edit session; the submit hook flushes them via
    // acknowledgeAISuggestions and the confirm card hides handled ones. Must sit
    // above the form so both the matcher (deep in the tier sections) and the
    // submit hook (called at the top of the form) read the same context.
    <SuggestionAcknowledgementProvider>
      <BeneficiaryDesignationsForm
        key={`${entityId ?? 'no-entity'}:${waterfallId ?? 'live'}`}
        initialFormValues={initialFormValues}
        onClose={onClose}
        onAfterSubmit={onAfterSubmit}
        onDidSave={onDidSave}
        isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
        waterfallId={isOnHypotheticalWaterfall ? waterfallId : undefined}
        waterfallDisplayName={waterfall?.displayName ?? null}
        entityType={entityType}
        entitySubtypeId={entitySubtypeId}
        accountDecedentClientId={accountDecedentClientId}
        entityIdOverride={entityIdOverride}
        displayNameOverride={displayNameOverride}
        firstDeathClientIdOverride={firstDeathClientIdOverride}
      />
    </SuggestionAcknowledgementProvider>
  );
}
